import { DateRange } from 'moment-range';
import { Dispatch, RefObject, SetStateAction, useEffect } from 'react';

import { applyCalendarDecorations, normalizeHighlightedDates } from './utils';

interface UseCalendarDecorationsOptions {
  calendarContainerRef: RefObject<HTMLDivElement>;
  highlightedDates: Array<string | Date>;
  inProgressAnchorRef: RefObject<Date>;
  isRolling: boolean;
  pendingAnchor?: Date;
  pendingDate?: Date;
  rollingPreviewRange?: DateRange;
  setRollingPreviewRange: Dispatch<SetStateAction<DateRange | undefined>>;
  singleDateSelection?: boolean;
  startDateSelected?: Date;
  valueRange: DateRange;
}

export const useCalendarDecorations = ({
  calendarContainerRef,
  highlightedDates,
  inProgressAnchorRef,
  isRolling,
  pendingAnchor,
  pendingDate,
  rollingPreviewRange,
  setRollingPreviewRange,
  singleDateSelection,
  startDateSelected,
  valueRange,
}: UseCalendarDecorationsOptions) => {
  useEffect(() => {
    const calendarContainer = calendarContainerRef.current;
    if (!calendarContainer) return undefined;

    const normalizedAdditionDates = normalizeHighlightedDates(highlightedDates);
    const pendingMarkerShouldBeVisible = !!pendingDate && !!startDateSelected && !singleDateSelection;

    const applyDecorations = () =>
      applyCalendarDecorations({
        calendarRoot: calendarContainer,
        inProgressAnchor: inProgressAnchorRef.current,
        normalizedAdditionDates,
        pendingDate: pendingMarkerShouldBeVisible ? pendingDate : undefined,
        rollingPreviewRange,
        valueRange,
      });

    let scheduledDecorationUpdate: number | undefined;
    const scheduleDecorationUpdate = (delay: number) => {
      if (scheduledDecorationUpdate !== undefined) return;

      scheduledDecorationUpdate = window.setTimeout(() => {
        scheduledDecorationUpdate = undefined;
        applyDecorations();
      }, delay);
    };

    applyDecorations();
    scheduleDecorationUpdate(10);

    const observer = new MutationObserver(() => {
      scheduleDecorationUpdate(0);
    });
    observer.observe(calendarContainer, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (scheduledDecorationUpdate !== undefined) window.clearTimeout(scheduledDecorationUpdate);
    };
  }, [
    calendarContainerRef,
    highlightedDates,
    inProgressAnchorRef,
    pendingDate,
    rollingPreviewRange,
    singleDateSelection,
    startDateSelected,
    valueRange,
  ]);

  useEffect(() => {
    if (!isRolling || !pendingAnchor) return undefined;

    const calendarContainer = calendarContainerRef.current;
    if (!calendarContainer) return undefined;

    const clearPreviewOutsideCalendar = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) return;

      const currentDate = event.target.closest('.DateRangePicker__Date');
      const nextDate =
        event.relatedTarget instanceof Element ? event.relatedTarget.closest('.DateRangePicker__Date') : undefined;
      if (!currentDate || (nextDate && calendarContainer.contains(nextDate))) return;

      setRollingPreviewRange(undefined);
    };

    calendarContainer.addEventListener('mouseout', clearPreviewOutsideCalendar);
    return () => calendarContainer.removeEventListener('mouseout', clearPreviewOutsideCalendar);
  }, [calendarContainerRef, isRolling, pendingAnchor, setRollingPreviewRange]);
};
